import AccountStore from "../stores/AccountStore"
import strings from "../localization/strings"
import {
	IHierarchyItem, IHierarchyDefinition, IHierarchyItemForAccountLevel, IInheritedHierarchySettingsItems,
	InitInheritedHierarchySettingsItems, IJobDocument
} from "../models"
import _ from "lodash"

export function detectValidHierarchy(hierarchyItemId: string): boolean {
	return hierarchyItemId ?
		AccountStore.getCurrentAccount().HierarchyItems.find(item => item.Id === hierarchyItemId) ?
			true :
			false :
		true
}

export function getHierarchyDefinitionByLevel(levelIndex: number): IHierarchyDefinition {
	const hierarchyDefinition = AccountStore.getCurrentAccount().HierarchyDefinitions.find(item => item.Level === levelIndex)
	return hierarchyDefinition ? hierarchyDefinition : null
}

export function getHierarchyItemById(hierarchyItemId: string): IHierarchyItem {
	const hierarchyItem = AccountStore.getCurrentAccount().HierarchyItems.find(item => item.Id === hierarchyItemId)
	return hierarchyItem ? hierarchyItem : null
}

export function getHierarchyDefinitionByDefinitionId(hierarchyDefinitionId: string): IHierarchyDefinition {
	const hierarchyDefinition = AccountStore.getCurrentAccount().HierarchyDefinitions.find(item => item.Id === hierarchyDefinitionId)
	return hierarchyDefinition ? hierarchyDefinition : null
}

export function getHierarchyItemsByDefinitionLevelId(hierarchyDefinitionId: string): IHierarchyItem[] {
	return AccountStore.getCurrentAccount().HierarchyItems.filter(item => item.HierarchyDefinitionId === hierarchyDefinitionId)
}

export function getHierarchyDefinitionByHierarchyItemId(hierarchyItemId: string): IHierarchyDefinition {
	const hierarchyItem = getHierarchyItemById(hierarchyItemId)
	return hierarchyItem ?
		getHierarchyDefinitionByDefinitionId(hierarchyItem.HierarchyDefinitionId) :
		null
}

export function isLeafNodeHierarchy(hierarchyItemId: string): boolean {
	const hierarchyItem = getHierarchyItemById(hierarchyItemId)
	if(!hierarchyItem) return false

	const hierarchyDefinition = getHierarchyDefinitionByHierarchyItemId(hierarchyItemId)
	if(!hierarchyDefinition) return false

	return hierarchyDefinition.Level === (AccountStore.getCurrentAccount().HierarchyDefinitions.length -1)
}

export function getHierarchyDefinitionNameByHierarchyId(hierarchyId: string): string {
	const hierarchyDefinition = getHierarchyDefinitionByHierarchyItemId(hierarchyId)
	return hierarchyDefinition ?
		hierarchyDefinition.Label :
		""
}

export function getHierarchyDefinitionNameByHierarchyDefinitionId(hierarchyDefinitionId: string): string {
	const hierarchyDefinition = getHierarchyDefinitionByDefinitionId(hierarchyDefinitionId)
	return hierarchyDefinition ?
		hierarchyDefinition.Label :
		""
}

export function getHierarchyDefinitionLevelByHierarchyItemId(hierarchyItemId: string): number {
	return hierarchyItemId ?
		getHierarchyDefinitionByHierarchyItemId(hierarchyItemId).Level :
		-1
}

export function getHierarchyItemNameByHierarchyId(hierarchyId: string): string {
	if (hierarchyId) {
		const hierarchy = getHierarchyItemById(hierarchyId)
		return hierarchy ?
			hierarchy.Label :
			""
	}
	else {
		return strings.Account
	}
}

export function getChildHierarchyItems(currentHierarchyItemId: string): IHierarchyItem[] {
	const hierarchyItemId = currentHierarchyItemId ? currentHierarchyItemId : null
	return AccountStore.getCurrentAccount().HierarchyItems.filter(item => item.ParentId === hierarchyItemId)
}

export function getHierarchyItemAndChildrens(hierarchyItemId: string): IHierarchyItem[] {
	return AccountStore.getCurrentAccount().HierarchyItems.filter((item) => item.HierarchyList && item.HierarchyList.includes(hierarchyItemId))
}

export function getFullPathLabel(hierarchyId: string): string {
	const hierarchyPathToRoot = getHierarchyPathToRoot(hierarchyId)
	if (hierarchyId && hierarchyPathToRoot.length === 0)
		return strings.Hierarchy_Removed
	let labelToRoot = strings.Account
	hierarchyPathToRoot.forEach((item, index) => {
		if (index > 0) {
			labelToRoot += ` -> ${item.hierarchyItem.Label} `
		}
	})
	return labelToRoot
}

export function getFullyQualifiedLevelNameById(hierarchyId: string): string {
	//if hierarchyItemId is DiffHierarchyLevel. in this case, should return empty
	// for example, GlobalPunchEdit
	if (hierarchyId === AccountStore.DiffHierarchyLevel)
		return "Difference Hierarchy"
	const hierarchyDefinitionName = getHierarchyDefinitionNameByHierarchyId(hierarchyId)
	const hierarchyName = getHierarchyItemNameByHierarchyId(hierarchyId)
	return hierarchyId ?
		hierarchyDefinitionName && hierarchyName ?
			`${hierarchyDefinitionName}: ${hierarchyName}` :
			strings.Hierarchy_Removed :
		`${strings.Account}: ${AccountStore.getCurrentAccount().Name}`
}

export function getHierarchyIdsForShowAllChildHierarchyData(showAllChildHierarchyData: boolean, hierarchyItemId: string): string[] {
	if (!AccountStore.getCurrentAccount()) return []
	let hierarchyIdsForShowAllChildHierarchyData: string[] = []
	if (showAllChildHierarchyData) {
		hierarchyIdsForShowAllChildHierarchyData = getHierarchyItemsAndChildrensIds([hierarchyItemId])
	}
	else {
		if (hierarchyItemId) {
			hierarchyIdsForShowAllChildHierarchyData.push(hierarchyItemId)
		}
		else {
			hierarchyIdsForShowAllChildHierarchyData.push(null)
		}
	}
	return hierarchyIdsForShowAllChildHierarchyData
}

export function getHierarchyItemsAndChildrensIds(hierarchyItemIds: string[]): string[] {
	let hierarchyItemsAndChildrensIds: string[] = []
	hierarchyItemIds.forEach(hierarchyId => {
		const hierarchiesForFilter = hierarchyId ? getHierarchyItemAndChildrens(hierarchyId) : AccountStore.getCurrentAccount().HierarchyItems
		if (!hierarchyId && !hierarchyItemsAndChildrensIds.includes(null)) {
			hierarchyItemsAndChildrensIds.push(null)
		}
		hierarchiesForFilter.forEach(item => !hierarchyItemsAndChildrensIds.includes(item.Id) && hierarchyItemsAndChildrensIds.push(item.Id))
	})
	return hierarchyItemsAndChildrensIds
}

export function checkHierarchyIsUnderFilterAccessHierarchy(filterAccessHierarchyId: string, hierarchyId: string): boolean {
	const childHierarchyIds = getHierarchyItemsAndChildrensIds([filterAccessHierarchyId])
	return childHierarchyIds.includes(hierarchyId)
}

export function getHierarchyPathToRoot(currentHierarchyItemId: string): IHierarchyItemForAccountLevel[] {
	const list = [
		{
			level: -1,
			hierarchyItem: {
				Label: AccountStore.getCurrentAccount().Name
			},
			hierarchyDefinition: {
				Label: strings.Account
			}
		} as IHierarchyItemForAccountLevel
	]

	if (!currentHierarchyItemId) {
		return list
	} else {
		const returnList = getHierarchyParentLevelRootData(currentHierarchyItemId, list)
		returnList.sort((a, b) => {
			return a.level > b.level ? 1 : b.level > a.level ? -1 : 0
		})
		return returnList
	}
}

function getHierarchyParentLevelRootData(currentHierarchyItemId: string, dataList: IHierarchyItemForAccountLevel[]) {
	const thisItem = getHierarchyItemById(currentHierarchyItemId)
	if (thisItem === null)
		return []

	const thisDefinition = getHierarchyDefinitionByDefinitionId(thisItem.HierarchyDefinitionId)
	if (thisDefinition === null)
		return []

	dataList.push({
		level: thisDefinition.Level,
		hierarchyItem: thisItem,
		hierarchyDefinition: thisDefinition
	})

	if (thisItem.ParentId !== null) {
		return getHierarchyParentLevelRootData(thisItem.ParentId, dataList)
	} else {
		return dataList
	}
}

export function getInheritedHierarchySettingItems(hierarchyJobItem: IHierarchyItemForAccountLevel | IHierarchyItem | IJobDocument): IInheritedHierarchySettingsItems {
	const inheritedHierarchySettingsItems = _.cloneDeep(InitInheritedHierarchySettingsItems)
	gatherInheritedHierarchySettingItems(hierarchyJobItem, inheritedHierarchySettingsItems)
	return inheritedHierarchySettingsItems
}

function gatherInheritedHierarchySettingItems(hierarchyJobItem: IHierarchyItemForAccountLevel | IHierarchyItem | IJobDocument, inheritedHierarchySettingsItems: IInheritedHierarchySettingsItems) {
	hierarchyJobItem = hierarchyJobItem && hierarchyJobItem.hasOwnProperty("AccountId") ?
		hierarchyJobItem as IJobDocument :
		getHierarchyItemById(hierarchyJobItem && (hierarchyJobItem as IHierarchyItem).Id) as IHierarchyItem

	const settingPropertyNames = [
		"AutoBreakSettings", "RoundingRulesSettings", "AutoTransferSettings", "PositionSelectionSettings", "CaliforniaOvertimeRule",
		"PayGroupSettings", "PunchRulesSettings", "ApprovedIPAddresses", "PunchQuestion", "MobileBreaks", "ScheduleBudgetUnitsSettings",
		"OTPolicySettings", "ApprovedIVRPhoneNumbers"
	]

	if (hierarchyJobItem && hierarchyJobItem.HierarchyItemSettings) {
		settingPropertyNames.forEach(settingPropertyName => {
			if (
				!inheritedHierarchySettingsItems[settingPropertyName] &&
				checkNotInheritedHierarchySetting(hierarchyJobItem as (IHierarchyItem | IJobDocument), settingPropertyName)
			)
				inheritedHierarchySettingsItems[settingPropertyName] = hierarchyJobItem
		})
	}

	if (
		inheritedHierarchySettingsItems.AutoBreakSettings &&
		inheritedHierarchySettingsItems.RoundingRulesSettings &&
		inheritedHierarchySettingsItems.AutoTransferSettings &&
		inheritedHierarchySettingsItems.PositionSelectionSettings &&
		inheritedHierarchySettingsItems.CaliforniaOvertimeRule &&
		inheritedHierarchySettingsItems.PayGroupSettings &&
		inheritedHierarchySettingsItems.PunchRulesSettings &&
		inheritedHierarchySettingsItems.ApprovedIPAddresses &&
		inheritedHierarchySettingsItems.PunchQuestion &&
		inheritedHierarchySettingsItems.MobileBreaks &&
		inheritedHierarchySettingsItems.ScheduleBudgetUnitsSettings &&
		inheritedHierarchySettingsItems.OTPolicySettings &&
		inheritedHierarchySettingsItems.ApprovedIVRPhoneNumbers 
	)
		return
	if (hierarchyJobItem && hierarchyJobItem.hasOwnProperty("AccountId")) {
		gatherInheritedHierarchySettingItems(getHierarchyItemById((hierarchyJobItem as IJobDocument).HierarchyId), inheritedHierarchySettingsItems)
	}
	else {
		if (hierarchyJobItem && (hierarchyJobItem as IHierarchyItem).ParentId) {
			gatherInheritedHierarchySettingItems(getHierarchyItemById((hierarchyJobItem as IHierarchyItem).ParentId), inheritedHierarchySettingsItems);
		}
	}
}

function checkNotInheritedHierarchySetting(hierarchyJobItem: IHierarchyItem | IJobDocument, settingPropertyName: string): boolean {
	if (!hierarchyJobItem.HierarchyItemSettings)
		return false
	let notInherited = false
	const hierarchyItemSetting = hierarchyJobItem.HierarchyItemSettings[settingPropertyName]
	switch (settingPropertyName) {
		case "AutoBreakSettings":
		case "RoundingRulesSettings":
		case "PunchRulesSettings":
		case "PunchQuestion":
		case "ScheduleBudgetUnitsSettings":
			notInherited = hierarchyItemSetting && hierarchyItemSetting.Use ? true : false
			break
		case "AutoTransferSettings":
		case "PositionSelectionSettings":
		case "CaliforniaOvertimeRule":
		case "PayGroupSettings":
		case "OTPolicySettings":
			notInherited = hierarchyItemSetting ? true : false
			break
		case "ApprovedIVRPhoneNumbers":
			notInherited = hierarchyItemSetting && hierarchyItemSetting.length > 0 ? true : false
			break
		case "ApprovedIPAddresses":
			notInherited = hierarchyItemSetting && hierarchyItemSetting.length > 0 ? true : false
			break
		case "MobileBreaks":
			notInherited = hierarchyItemSetting && hierarchyItemSetting.filter(i => !i.Deleted).length > 0 ? true : false
	}

	return notInherited
}

export function getHierarchyVariations(hierarchyId: string, settingPropertyName: string): IHierarchyItem[] {
	const allChildHierarchyItems = hierarchyId ?
		getHierarchyItemAndChildrens(hierarchyId).filter(i => i.Id !== hierarchyId) :
		AccountStore.getCurrentAccount().HierarchyItems.filter(i => i.Active)
	const notInheritedItems = allChildHierarchyItems.filter(i => checkNotInheritedHierarchySetting(i, settingPropertyName))

	notInheritedItems.sort((a, b) => {
		const aLevel = getHierarchyDefinitionByHierarchyItemId(a.Id).Level
		const bLevel = getHierarchyDefinitionByHierarchyItemId(b.Id).Level
		if (aLevel < bLevel) return -1;
		if (aLevel > bLevel) return 1;

		// If level are equal, compare by label
		if (a.Label < b.Label) return -1
		if (a.Label > b.Label) return 1

		return 0
	})
	return notInheritedItems
}